import type { ReviewStatus, ReviewStep } from '@shared/domain/review';
import type { ReviewAnswer, ReviewSection } from './types/types';
import { Box, Typography } from '@mui/material';
import DynamicIcon, { IconName } from '@shared/components/Icon';
import { theme } from '@shared/theme';
import EditReviewSection from '../review/edit-review/EditReviewSection';
import RatingsReader from './components/RatingsReader';
import ApproversListCard from './components/ReviewApproversListCard';
import ReviewNotFound from './components/ReviewNotFound';
import SectionHeaderReader from './components/SectionHeaderReader';
import ReviewReaderBasicQuestion from './ReviewReaderBasicQuestion';
import { InfoContainer } from './styles/ReviewReader.styles';

function CommentSection({ commentEntries }: { commentEntries: [string, ReviewAnswer][] }) {
  return (
    commentEntries.length > 0 && (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: theme.spacing(1),
          marginTop: theme.spacing(1),
          [theme.breakpoints.down('md')]: {
            gridTemplateColumns: 'repeat(1, 1fr)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {commentEntries
          .filter(([_key, value]) => value.answer !== 'N/A' && value.answer !== null)
          .map(([key, value]) => (
            <Box
              key={key}
              id={`review-field-${key}`}
              data-field={key}
              className="review-field"
              sx={{
                background: '#f9fbfd',
                borderLeft: `4px solid #0b74da`,
                padding: theme.spacing(1),
                borderRadius: theme.spacing(1.5),
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(0.5),
                minHeight: 120,
                [theme.breakpoints.down('md')]: {
                  marginBottom: theme.spacing(1),
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 16,
                  color: '#0b74da',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <DynamicIcon iconName={IconName.ChatBubble} />
                {value.label}
              </Typography>
              <Box
                component="span"
                sx={{
                  fontSize: 15,
                  color: '#444',
                  lineHeight: 1.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(0.5),
                  fontStyle: 'italic',
                  whiteSpace: 'pre-wrap',
                }}
              >
                <DynamicIcon iconName={IconName.FormatQuote} />
                {value.answer || 'N/A'}
              </Box>
            </Box>
          ))}
      </Box>
    )
  );
}

function BasicReviewReader({
  section,
  sectionTitle,
  sectionKey,
  scrollToTop,
  setIsEditMode: _setIsEditMode,
  isEditMode = false,
  canEdit = false,
  defaultReview,
  updateReviewStatus,
}: {
  section?: ReviewSection | null;
  sectionTitle: string;
  sectionKey: ReviewStep;
  scrollToTop: () => void;
  setIsEditMode: (isEditMode: boolean) => void;
  isEditMode: boolean;
  canEdit?: boolean;
  defaultReview?: any;
  updateReviewStatus?: (status: ReviewStatus) => void;
}) {
  const commentEntries = Object.entries(section ?? {}).filter(([key]) => key.toLowerCase().includes('comment')) as [
    string,
    ReviewAnswer,
  ][];

  const nonCommentEntries = Object.entries(section ?? {}).filter(
    ([key, value]) =>
      !key.toLowerCase().includes('comment') &&
      !key.toLowerCase().includes('list') &&
      key !== 'ratings' &&
      value.answer !== 'N/A' &&
      value.answer !== null,
  );

  const listEntries = Object.entries(section ?? {}).filter(
    ([key, value]) => key.toLowerCase().includes('list') && Array.isArray(value.answer) && value.answer.length > 0,
  ) as [string, ReviewAnswer][];

  // Check if section is empty (no data filled in)
  const isSectionEmpty =
    nonCommentEntries.length === 0 && commentEntries.length === 0 && listEntries.length === 0 && !section?.ratings;

  if (!section) {
    return <ReviewNotFound />;
  }

  return (
    <Box
      sx={{
        padding: theme.spacing(2),
        width: '100%',
      }}
    >
      <InfoContainer>
        <SectionHeaderReader
          headerSubtext={sectionTitle}
          scrollToTop={scrollToTop}
          canEdit={canEdit}
          isEditMode={isEditMode}
          onToggleEditMode={() => _setIsEditMode(!isEditMode)}
          sectionKey={String(sectionKey)}
          scrollToSection={scrollToTop}
        />
        {isEditMode ? (
          <EditReviewSection
            sectionKey={sectionKey as unknown as ReviewStep}
            review={defaultReview}
            updateReviewStatus={updateReviewStatus}
          />
        ) : isSectionEmpty ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: theme.spacing(5),
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
              borderRadius: theme.spacing(2),
              border: '2px dashed #d0d7de',
              margin: `${theme.spacing(2)} 0`,
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#a8b4c2',
                background: 'linear-gradient(135deg, #f0f4f8 0%, #e0e6ed 100%)',
              },
            }}
          >
            <Box
              sx={{
                fontSize: 64,
                color: '#a0aec0',
                marginBottom: theme.spacing(2),
              }}
            >
              <DynamicIcon iconName={IconName.Info} size="large" />
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                marginBottom: theme.spacing(0.5),
              }}
            >
              Section Not Completed
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                textAlign: 'center',
                maxWidth: 400,
              }}
            >
              This section hasn't been filled out yet
            </Typography>
          </Box>
        ) : (
          <>
            {listEntries.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  margin: `${theme.spacing(2)} 0`,
                  [theme.breakpoints.down('md')]: {
                    flexDirection: 'column',
                  },
                }}
              >
                {listEntries.map(([key, value]) => (
                  <ApproversListCard key={key} label={value.label} data={value.answer as string[]} />
                ))}
              </Box>
            )}

            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }}
              gap={2}
              sx={{
                margin: `${theme.spacing(2)} 0`,
              }}
            >
              {nonCommentEntries.map(([key, value]) => (
                <ReviewReaderBasicQuestion
                  key={key}
                  label={value.label}
                  answer={value.answer as string}
                  iconGroup={value.iconGroup}
                />
              ))}
            </Box>

            {commentEntries.length > 0 && <CommentSection commentEntries={commentEntries} />}

            {section.ratings && <RatingsReader ratings={Array.isArray(section.ratings) ? section.ratings : []} />}
          </>
        )}
      </InfoContainer>
    </Box>
  );
}

export default BasicReviewReader;
